import type { AuthConfig } from "@src/domain/types/AuthConfig";

/**
 * Application Configuration - Configuration layer
 * Centralized configuration management using static methods
 * Following Single Responsibility Principle - handles only application configuration
 */
export class AppConfig {
  /**
   * Get the application name
   */
  static getAppName(): string {
    return import.meta.env.VITE_APP_NAME || "DM Admin Webapp";
  }

  /**
   * Get the application version
   */
  static getAppVersion(): string {
    return import.meta.env.VITE_APP_VERSION || "1.0.0";
  }

  /**
   * Get the base URL of the application
   */
  static getBaseUrl(): string {
    return import.meta.env.VITE_BASE_URL || globalThis.location.origin;
  }

  /**
   * Get the API base URL
   */
  static getApiUrl(): string {
    const apiUrl = import.meta.env.VITE_API_URL;
    if (!apiUrl) {
      throw new Error("API URL must be configured (VITE_API_URL)");
    }
    return apiUrl;
  }

  /**
   * Get the API base URL
   */
  static getGraphQLUrl(): string {
    const graphQLUrl = import.meta.env.VITE_GRAPHQL_URL;
    if (!graphQLUrl) {
      throw new Error("GraphQL URL must be configured (VITE_GRAPHQL_URL)");
    }
    return graphQLUrl;
  }

  /**
   * Get the application environment (development, production, etc.)
   */
  static getEnvironment(): string {
    return import.meta.env.VITE_ENVIRONMENT as string;
  }

  /**
   * Check if the application is running in development mode
   */
  static isDevelopment(): boolean {
    return this.getEnvironment() === "development";
  }

  /**
   * Check if the application is running in local mode
   */
  static isLocal(): boolean {
    return this.getEnvironment() === "local";
  }

  /**
   * Check if the application is running in beta mode
   */
  static isBeta(): boolean {
    return this.getEnvironment() === "beta";
  }

  /**
   * Check if the application is running in production mode
   */
  static isProduction(): boolean {
    return this.getEnvironment() === "production";
  }

  /**
   * Get the application title
   */
  static getAppTitle(): string {
    return import.meta.env.VITE_APP_TITLE || "DM Admin";
  }

  /**
   * Get the application description
   */
  static getAppDescription(): string {
    return import.meta.env.VITE_APP_DESCRIPTION || "DM Admin Web Application";
  }

  /**
   * Get the pagination default page size
   */
  static getDefaultPageSize(): number {
    const pageSize = import.meta.env.VITE_DEFAULT_PAGE_SIZE;
    return pageSize ? Number.parseInt(pageSize, 10) : 10;
  }

  /**
   * Get the maximum file upload size in bytes
   */
  static getMaxFileUploadSize(): number {
    const maxSize = import.meta.env.VITE_MAX_FILE_UPLOAD_SIZE;
    return maxSize ? Number.parseInt(maxSize, 10) : 5 * 1024 * 1024; // Default: 5MB
  }

  /**
   * Get the session timeout in milliseconds
   */
  static getSessionTimeout(): number {
    const timeout = import.meta.env.VITE_SESSION_TIMEOUT;
    return timeout ? Number.parseInt(timeout, 10) : 30 * 60 * 1000; // Default: 30 minutes
  }

  /**
   * Check if debug mode is enabled
   */
  static isDebugMode(): boolean {
    return (
      import.meta.env.VITE_DEBUG === "true" ||
      this.isDevelopment() ||
      this.isLocal()
    );
  }

  /**
   * Get the log level
   */
  static getLogLevel(): string {
    return (
      import.meta.env.VITE_LOG_LEVEL as string
    );
  }

  /**
   * Get Auth0 domain
   */
  static getAuth0Domain(): string {
    const domain = import.meta.env.VITE_AUTH0_DOMAIN;
    if (!domain) {
      throw new Error("Auth0 domain must be configured (VITE_AUTH0_DOMAIN)");
    }
    return domain;
  }

  /**
   * Get Auth0 client ID
   */
  static getAuth0ClientId(): string {
    const clientId = import.meta.env.VITE_AUTH0_CLIENT_ID;
    if (!clientId) {
      throw new Error(
        "Auth0 client ID must be configured (VITE_AUTH0_CLIENT_ID)",
      );
    }
    return clientId;
  }

  /**
   * Get Auth0 redirect URI
   */
  static getAuth0RedirectUri(): string {
    return (
      import.meta.env.VITE_AUTH0_REDIRECT_URI ||
      `${this.getBaseUrl()}/auth/callback`
    );
  }

  /**
   * Get Auth0 audience (optional)
   */
  static getAuth0Audience(): string | undefined {
    return import.meta.env.VITE_AUTH0_AUDIENCE;
  }

  /**
   * Get Auth0 scope
   */
  static getAuth0Scope(): string {
    return import.meta.env.VITE_AUTH0_SCOPE || "openid profile email";
  }

  /**
   * Get complete Auth0 configuration
   */
  static getAuth0Config(): AuthConfig {
    return {
      domain: this.getAuth0Domain(),
      clientId: this.getAuth0ClientId(),
      authorizationParams: {
        redirect_uri: this.getAuth0RedirectUri(),
        ...(this.getAuth0Audience() && { audience: this.getAuth0Audience() }),
        scope: this.getAuth0Scope(),
      },
    };
  }

  /**
   * Get the default pagination limit
   */
  static getDefaultPaginationLimit(): number {
    const raw = import.meta.env.VITE_DEFAULT_PAGINATION_LIMIT ?? "10";
    const parsed = Number(raw);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : 10;
  }

  /**
   * Get the current logger
   */
  static getCurrentLogger(): string {
    return import.meta.env.VITE_CURRENT_LOGGER || "console";
  }

  /**
   * Get the sentry DSN
   */
  static getSentryDsn(): string {
    return import.meta.env.VITE_SENTRY_DSN || "";
  }

  /**
   * Get the sentry project
   */
  static getSentryProject(): string {
    return import.meta.env.VITE_SENTRY_PROJECT || "";
  }

  /**
   * Get the sentry environment
   */
  static getSentryEnvironment(): string {
    return import.meta.env.VITE_SENTRY_ENVIRONMENT || "development";
  }

  /**
   * Get the sentry auth token
   */
  static getSentryAuthToken(): string {
    return import.meta.env.VITE_SENTRY_AUTH_TOKEN || "";
  }

  /**
   * Get all configuration as an object (useful for debugging)
   */
  static getAllConfig(): Record<string, unknown> {
    return {
      appName: this.getAppName(),
      appVersion: this.getAppVersion(),
      baseUrl: this.getBaseUrl(),
      apiUrl: this.getApiUrl(),
      environment: this.getEnvironment(),
      appTitle: this.getAppTitle(),
      appDescription: this.getAppDescription(),
      defaultPageSize: this.getDefaultPageSize(),
      maxFileUploadSize: this.getMaxFileUploadSize(),
      sessionTimeout: this.getSessionTimeout(),
      isDevelopment: this.isDevelopment(),
      isProduction: this.isProduction(),
      isDebugMode: this.isDebugMode(),
      logLevel: this.getLogLevel(),
      auth0: {
        domain: this.getAuth0Domain(),
        clientId: this.getAuth0ClientId(),
        redirectUri: this.getAuth0RedirectUri(),
        audience: this.getAuth0Audience(),
        scope: this.getAuth0Scope(),
      },
      defaultPaginationLimit: this.getDefaultPaginationLimit(),
      currentLogger: this.getCurrentLogger(),
      sentryDsn: this.getSentryDsn(),
      sentryProject: this.getSentryProject(),
      sentryEnvironment: this.getSentryEnvironment(),
      sentryAuthToken: this.getSentryAuthToken(),
    };
  }
}
